import {
	CSSProperties,
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	Ref,
	useCallback,
	useMemo,
	useState
} from "react";
import classNames from "classnames";
import {
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { Coordinate } from "client/Misc/commonTypes";
import {
	useDragAndDropEvents,
	DragOverEventArgs,
	DragOverEventResult,
	DropEventArgs,
	useCallbackWithoutArguments
} from "client/Hooks";
import {
	Icon,
	IconColor,
	FontIconName
} from "../Icon/Icon";
import { never } from "client_legacy/ndaw/errors";
import styles from"./TreeAccordionItem.module.less";
import { Navigation, NavigationOptions } from "client/Navigation";
import { NavigationLink } from "../NavigationLink/NavigationLink";

export type TreeAccordionItemProps = {
	indentionLevel?: number;
	isHeader?: boolean;
	isSelected?: boolean;
	icon?: FontIconName;
	iconColor?: IconColor;
	stretchContentHeight?: boolean;
	navigation?: Navigation | string;
	navigationOptions?: NavigationOptions;
	onClick?(): void;
	onContextMenu?(position: Coordinate): void;
	onDragOver?(args: DragOverEventArgs): DragOverEventResult;
	onLongDragOver?(args: DragOverEventArgs): void;
	onDrop?(args: DropEventArgs): Promise<void> | void;
	title?: string;
	trackingId?: string;
	isDisabled?: boolean;
	action?: ReactElement;
	tabIndex?: number;
	ref?: Ref<HTMLElement>;
} & (
	{
		expansionToggle?: "none" | "hidden";
	}
	|
	{
		expansionToggle: "visible";
		isExpanded: boolean;
		onToggleExpansion(): void;
	}
) & (
	{
		navigation: Navigation | string;
		navigationOptions?: NavigationOptions;
	}
	|
	{
		navigation?: undefined;
	}
);

export const TreeAccordionItem = (props: PropsWithChildren<TreeAccordionItemProps>): ReactElement => {
	const {
		isSelected,
		indentionLevel = 0,
		isHeader = false,
		icon,
		iconColor,
		stretchContentHeight = false,
		navigation,
		navigationOptions,
		onClick,
		onContextMenu,
		onDrop,
		onDragOver,
		onLongDragOver,
		title,
		trackingId,
		isDisabled,
		action,
		tabIndex,
		ref,
		children
	} = props;

	const {
		isDropTarget,
		...dragAndDropEventHandlers
	} = useDragAndDropEvents({
		onDragOver,
		onLongDragOver,
		onDrop
	});

	const [isExpansionToggleHovered, setIsExpansionToggleHovered] = useState(false);

	const contextMenuHandler = (args: MouseEvent): void => {
		if (onContextMenu) {
			args.preventDefault();
			onContextMenu({
				x: args.clientX,
				y: args.clientY
			});
		}
	};

	const style = useMemo(
		(): CSSProperties => {
			const result: CSSProperties = {};
			result["--indention-level"] = indentionLevel;
			return result;
		},
		[indentionLevel]
	);

	const clickHandler: (() => void) | undefined = useCallbackWithoutArguments(onClick);

	const renderExpansionToggle = (): ReactElement | null => {
		switch (props.expansionToggle) {
			case undefined:
			case "none":
				return null;
			case "hidden":
				return (
					<div
						className={
							styles.expansionToggleContainer
						}
					/>
				);
			case "visible":
				return (
					<div
						className={styles.expansionToggleContainer}
					>
						<ExpansionToggle
							isExpanded={props.isExpanded}
							onIsHovered={setIsExpansionToggleHovered}
							onClick={props.onToggleExpansion}
							isDisabled={isDisabled}
							trackingId="expansion-toggle"
						/>
					</div>
				);
			default:
				never(props);
		}
	};

	const baseProps = {
		...dragAndDropEventHandlers,
		className: classNames(
			styles.root,
			{
				[styles.root_header]: isHeader || indentionLevel === 0,
				[styles.root_selected]: isSelected,
				[styles.root_disabled]: isDisabled,
				[styles.root_dropTarget]: isDropTarget,
				[styles.root_noHover]: isExpansionToggleHovered || isNil(isSelected),
				[styles.root_interactive]: isNotNil(onClick) && !isDisabled
			}
		),
		onClick: !isDisabled ? clickHandler : undefined,
		onContextMenu: contextMenuHandler,
		title: title,
		"aria-expanded": props.expansionToggle === "visible" && props.isExpanded,
		"aria-selected": isSelected,
	};

	const renderChildren = (): ReactElement => (
		<div className={styles.container} style={style}>
			{icon && (
				<div className={styles.icon}>
					<Icon
						value={{
							kind: "font",
							name: icon,
							color: isSelected || isDisabled
								? undefined
								: iconColor
						}}
					/>
				</div>
			)}
			<div
				{...setTrackingId("content")}
				className={classNames(
					styles.content,
					{
						[styles.content_stretchHeight]: stretchContentHeight
					}
				)}>
				{children}
			</div>
			<div className={styles.actionAndCollapseContainer}>
				{!isDisabled && action}
				{renderExpansionToggle()}
			</div>
		</div>
	);

	if (isNotNil(navigation) && !isDisabled) {
		return (
			<NavigationLink
				{...baseProps}
				ref={ref as Ref<HTMLAnchorElement>}
				navigation={navigation}
				options={navigationOptions}
				trackingId={trackingId}
				tabIndex={tabIndex}
			>
				{renderChildren()}
			</NavigationLink>
		);
	} else {
		return (
			<div
				{...setTrackingId(trackingId)}
				{...baseProps}
				ref={ref as Ref<HTMLDivElement>}
				tabIndex={tabIndex}
			>
				{renderChildren()}
			</div>
		);
	}
};
TreeAccordionItem.displayName = "TreeAccordion.Item";

export type ExpansionToggleProps = {
	isExpanded: boolean;
	onClick(): void;
	onIsHovered(isHovered: boolean): void;
	trackingId?: string;
	isDisabled?: boolean;
};

const ExpansionToggle = (props: ExpansionToggleProps): ReactElement => {
	const {
		isExpanded,
		onClick,
		onIsHovered,
		trackingId,
		isDisabled = false
	} = props;

	const clickHandler = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();
			e.preventDefault();
			if (!isDisabled) {
				onClick();
			}
		},
		[isDisabled, onClick]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={
				classNames(
					styles.expansionToggle,
					{
						[styles.expansionToggle_expanded]: isExpanded,
						[styles.expansionToggle_disabled]: isDisabled
					}
				)
			}
			onClick={clickHandler}
			onMouseEnter={()=> onIsHovered(true)}
			onMouseLeave={()=> onIsHovered(false)}
		>
			<div className={styles.expansionToggleIcon}>
				<Icon value="chevron-down" />
			</div>
		</div>
	);
};
ExpansionToggle.displayName = "ExpansionToggle";
